import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import AdminDataBrowser from "@/components/AdminDataBrowser";
import { ImpactReports } from "@/components/ImpactReports";
import { GraphicDownloads } from "@/components/GraphicDownloads";
import { LEVEL_STYLES } from "@/lib/enums";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [notifs, setNotifs] = useState([]);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const loadNotifs = () =>
    api.get("/admin/notifications?limit=50").then((r) => setNotifs(r.data)).catch(() => {});

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/summary").then(
        (r) => setData(r.data),
        (e) => setErr(e.response?.data?.detail || "Failed to load"),
      );
      loadNotifs();
    }
  }, [user]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/run-notifications");
      setRunResult(r);
      await loadNotifs();
    } catch (e) {
      setRunResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  const sendImpact = async () => {
    try {
      const { data: r } = await api.post("/admin/send-impact-report");
      setRunResult({ impact: r });
      await loadNotifs();
    } catch (e) {
      setRunResult({ error: e.response?.data?.detail || e.message });
    }
  };

  if (user === null) return <div className="p-12 eyebrow text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  if (err) return <div className="p-12 text-center text-[#9B2C2C]">{err}</div>;
  if (!data) return <div className="p-12 eyebrow text-center">Loading admin summary…</div>;

  const t = data.totals;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-8" data-testid="admin-dashboard">
      <div className="fade-up">
        <p className="eyebrow">Program · Admin</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[#1C201F]">Program overview</h1>
        <p className="text-sm text-[#5C6661] mt-2 max-w-2xl">
          Aggregated across all registered households. All readings shown; the
          Action-Level threshold used for the &ldquo;≥ 4 pCi/L&rdquo; count is EPA&rsquo;s standard.
        </p>
      </div>

      <ImpactReports />
      <GraphicDownloads />

      <p className="eyebrow -mb-4" data-testid="admin-summary-label">All accounts (including staff)</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 fade-up delay-1">
        {[
          { k: "Users", v: t.users },
          { k: "Readings", v: t.readings },
          { k: "Readings ≥ 4 pCi/L", v: t.readings_over_action },
          { k: "Mitigations completed", v: t.mitigations },
        ].map((s) => (
          <div key={s.k} className="card-flat">
            <p className="eyebrow">{s.k}</p>
            <p className="font-serif text-4xl mt-2 text-[#1C201F]">{s.v}</p>
          </div>
        ))}
      </div>

      <div className="card-flat fade-up delay-2">
        <h3 className="font-serif text-xl">Readings by county / ZIP</h3>
        <p className="text-xs text-[#5C6661] mt-1">Sorted by number of readings.</p>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#5C6661] border-b border-[#E8ECEA]">
                <th className="py-2 pr-3">State</th>
                <th className="py-2 pr-3">ZIP</th>
                <th className="py-2 pr-3">Readings</th>
                <th className="py-2 pr-3">Avg pCi/L</th>
                <th className="py-2 pr-3">Over 4.0</th>
                <th className="py-2 pr-3">Distribution</th>
              </tr>
            </thead>
            <tbody>
              {data.by_zip.map((row) => (
                <tr key={`${row.state}-${row.zip}`} className="border-b border-[#E8ECEA]/60" data-testid="admin-zip-row">
                  <td className="py-3 pr-3 text-[#1C201F]">{row.state}</td>
                  <td className="py-3 pr-3 text-[#1C201F]">{row.zip}</td>
                  <td className="py-3 pr-3 text-[#1C201F]">{row.readings}</td>
                  <td className="py-3 pr-3 text-[#5C6661]">{row.avg_pci.toFixed(1)}</td>
                  <td className="py-3 pr-3 text-[#1C201F]">{row.over_action}</td>
                  <td className="py-3 pr-3">
                    <div className="flex gap-0.5">
                      {(row.readings_list || []).map((v, i) => {
                        const key =
                          v < 2 ? "below-action" :
                          v < 4 ? "consider" :
                          v < 8 ? "action" :
                          v < 20 ? "high" : "very-high";
                        const s = LEVEL_STYLES[key];
                        return (
                          <span key={i} title={`${v.toFixed(1)} pCi/L`}
                            className="inline-block w-2 h-4 rounded-sm"
                            style={{ background: s.border }} />
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
              {data.by_zip.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-[#5C6661]">No readings yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-flat fade-up delay-3">
        <h3 className="font-serif text-xl">Mitigations by state</h3>
        <ul className="mt-4 space-y-2 text-sm">
          {data.mitigations_by_state.map((m) => (
            <li key={m.state} className="flex items-center justify-between border-b border-[#E8ECEA]/60 py-2">
              <span className="text-[#1C201F]">{m.state}</span>
              <span className="text-[#5C6661]">{m.count} mitigation{m.count === 1 ? "" : "s"}</span>
            </li>
          ))}
          {data.mitigations_by_state.length === 0 && (
            <li className="text-[#5C6661] py-2">No mitigations recorded yet.</li>
          )}
        </ul>
      </div>

      <div className="card-flat fade-up delay-4" data-testid="admin-notifications">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <h3 className="font-serif text-xl">Notifications log</h3>
            <p className="text-xs text-[#5C6661] mt-1">
              Every reminder, follow-up, and assistance-handoff email. Idempotent — running again is safe.
            </p>
          </div>
          <button
            onClick={runNow}
            disabled={running}
            className="btn-primary !py-2 !px-4 text-sm"
            data-testid="admin-run-notifications"
          >
            {running ? "Running…" : "Run notifications now"}
          </button>
          <button
            onClick={sendImpact}
            className="btn-secondary !py-2 !px-4 text-sm ml-2"
            data-testid="admin-send-impact"
          >
            Send impact report
          </button>
        </div>
        {runResult && (
          <p className="text-xs text-[#2F524A] font-semibold mt-3" data-testid="admin-run-result">
            {runResult.error
              ? `Error: ${runResult.error}`
              : `Retest: ${runResult.retest_sent} · Follow-ups: ${runResult.followup_sent} · Skipped: ${runResult.skipped} · Failed: ${runResult.failed}`}
          </p>
        )}
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#5C6661] border-b border-[#E8ECEA]">
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Kind</th>
                <th className="py-2 pr-3">To</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {notifs.map((n) => (
                <tr key={n.id} className="border-b border-[#E8ECEA]/60" data-testid="admin-notification-row">
                  <td className="py-2 pr-3 text-[#5C6661]">{n.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 pr-3 text-[#1C201F]">{n.kind}</td>
                  <td className="py-2 pr-3 text-[#1C201F]">{n.to}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${
                      n.status === "sent" ? "border-[#2F524A] text-[#2F524A]" :
                      n.status === "failed" ? "border-[#9B2C2C] text-[#9B2C2C]" :
                      "border-[#E8ECEA] text-[#5C6661]"
                    }`}>
                      {n.status}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-[10px] text-[#5C6661] max-w-md truncate">
                    {n.provider_id || n.error || "—"}
                  </td>
                </tr>
              ))}
              {notifs.length === 0 && (
                <tr><td colSpan={5} className="text-center text-[#5C6661] py-6">No notifications yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-flat fade-up delay-5" data-testid="admin-domain-setup">
        <p className="eyebrow">Email delivery setup</p>
        <h3 className="font-serif text-xl mt-1">
          Verify a sending domain to email real homeowners
        </h3>
        <p className="text-sm text-[#5C6661] mt-2 leading-relaxed">
          Resend is currently in sandbox mode — only emails to{" "}
          <code className="text-[#1C201F]">info@citizens4radonreduction.org</code>{" "}
          actually deliver. Reminders and follow-ups to other addresses log as{" "}
          <span className="text-[#9B2C2C] font-semibold">failed</span> in the
          Notifications log until you verify a sending domain.
        </p>
        <ol className="mt-4 space-y-2 text-sm text-[#1C201F] list-decimal list-inside">
          <li>Go to <a className="text-[#2F524A] underline font-semibold" href="https://resend.com/domains" target="_blank" rel="noopener noreferrer">resend.com/domains</a> → <b>Add Domain</b> (e.g. <code>citizens4radonreduction.org</code>).</li>
          <li>Copy the DNS records Resend shows (typically 1 MX + 2 TXT records).</li>
          <li>Add them at your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.). Propagation ~5–30 min.</li>
          <li>Once the domain shows <b>Verified</b>, update <code>SENDER_EMAIL</code> in production env to something like <code>Radon Action &lt;no-reply@citizens4radonreduction.org&gt;</code>.</li>
          <li>Redeploy — real emails will start delivering to every homeowner.</li>
        </ol>
      </div>

      {/* Full data browser: all submissions with filters + CSV export */}
      <AdminDataBrowser />
    </div>
  );
};

export default AdminDashboard;
